import { useState, useRef, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { Preview3D } from './Preview3D';
import { TimelineContainer } from './timeline/TimelineContainer';
import { TabBar } from './tabs/TabBar';
import { TimelineControls } from './timeline/TimelineControls';
import { PlaybackEngine } from './timeline/PlaybackEngine';
import { UndoRedoControls } from './history/UndoRedoControls';
import { SceneExportImport } from './SceneExportImport';
import { SaveButton } from './SaveButton';
import { CloudSaveButton } from './CloudSaveButton';
import { PuppetSelector } from './configurator/PuppetSelector';
import { PuppetPositionControls } from './configurator/PuppetPositionControls';
import { ErrorBoundary } from './common/ErrorBoundary';
import { useHistoryTracking } from '../hooks/useHistoryTracking';
import { useUnsavedChangesWarning } from '../hooks/useUnsavedChangesWarning';
import { usePuppetStore } from '../stores/puppetStore';

/**
 * Main application layout component
 * Three-panel structure: fixed sidebar on left, resizable 3D preview and timeline on right
 */
export function MainLayout() {
  // Enable automatic history tracking for timeline actions
  useHistoryTracking();

  // Enable unsaved changes warning before page unload
  useUnsavedChangesWarning();

  // Initialize puppet store with default puppet if none exists
  useEffect(() => {
    const { activePuppet, setActivePuppet, getDefaultPuppet } =
      usePuppetStore.getState();
    if (!activePuppet) {
      setActivePuppet(getDefaultPuppet());
    }
  }, []);

  // Split ratio for 3D preview vs timeline (0-1, where 0.65 = 65% preview, 35% timeline)
  const [splitRatio, setSplitRatio] = useState(0.65);
  const [isDragging, setIsDragging] = useState(false);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Handle mouse down on divider to start dragging
   */
  const handleMouseDown = () => {
    setIsDragging(true);
  };

  /**
   * Handle mouse move during divider drag
   * Calculates new split ratio based on mouse position
   */
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();

      // Calculate mouse position relative to container
      const y = e.clientY - rect.top;
      const newRatio = y / rect.height;

      // Clamp ratio between 0.6 (60%) and 0.7 (70%) as per spec
      const clampedRatio = Math.max(0.6, Math.min(0.7, newRatio));
      setSplitRatio(clampedRatio);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-dark-bg">
      {/* PlaybackEngine - Headless component that manages playback animation loop */}
      <PlaybackEngine loopEnabled={loopEnabled} />

      {/* Left Sidebar - Fixed width */}
      <aside
        className="flex-shrink-0 border-r panel-border"
        style={{ width: 'var(--spacing-sidebar)' }}
      >
        <Sidebar />
      </aside>

      {/* Right Panel - Resizable 3D Preview and Timeline */}
      <main
        id="main-content"
        className="flex flex-col flex-1 min-w-0"
        ref={containerRef}
        tabIndex={-1}
      >
        {/* 3D Preview Section with Error Boundary (Task 10.8) */}
        <div
          className="flex-shrink-0 border-b panel-border"
          style={{ height: `${splitRatio * 100}%` }}
        >
          <ErrorBoundary
            onReset={() => {
              console.log('3D Preview reset');
            }}
          >
            <Preview3D />
          </ErrorBoundary>
        </div>

        {/* Resizable Divider */}
        <div
          className={`
            flex-shrink-0 h-1 bg-dark-border cursor-row-resize
            hover:bg-body-head transition-colors
            ${isDragging ? 'bg-body-head' : ''}
          `}
          style={{
            transition: isDragging ? 'none' : 'var(--transition-fast)',
            cursor: 'row-resize',
            userSelect: 'none',
          }}
          onMouseDown={handleMouseDown}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize divider between 3D preview and timeline"
          aria-valuemin={60}
          aria-valuemax={70}
          aria-valuenow={Math.round(splitRatio * 100)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSplitRatio((prev) => Math.max(0.6, prev - 0.01));
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSplitRatio((prev) => Math.min(0.7, prev + 0.01));
            }
          }}
        />

        {/* Timeline Section */}
        <div className="flex-1 min-h-0">
          <div className="h-full w-full flex flex-col panel-bg">
            {/* Tab Bar */}
            <TabBar />

            {/* Timeline Controls with Puppet Selector, Position Controls, Export/Import, and Undo/Redo */}
            <div className="flex-shrink-0 border-b panel-border bg-[#262626] flex items-center justify-between px-4 py-2">
              <TimelineControls
                loopEnabled={loopEnabled}
                onLoopToggle={setLoopEnabled}
              />
              <div className="flex items-center gap-4">
                <PuppetSelector />
                <PuppetPositionControls />
                <SaveButton />
                <CloudSaveButton />
                <SceneExportImport />
                <UndoRedoControls />
              </div>
            </div>

            {/* TimelineContainer - The main scrollable timeline area */}
            <div className="flex-1 min-h-0">
              <ErrorBoundary
                onReset={() => {
                  console.log('Timeline reset');
                }}
              >
                <TimelineContainer />
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
